import Uppy from '@uppy/core'
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import { Dashboard } from '@uppy/react'
import Tus from '@uppy/tus'
import React, { useEffect, useState } from 'react'
import links from '../../../../config/links'
import Button from '../../../common/Button'
import Icon from '../../../common/Icon'
import { useTranslation } from '../../../../lib/i18n'

const UppyUploadButton = ({
  allowVideoUpload,
  setVideoUppy,
  setVideoId,
  getValues,
  handleSetResourceOption,
}) => {
  const { t } = useTranslation()

  const configureUppy = () => {
    const uppy = new Uppy({
      restrictions: {
        maxFileSize: 2147483648, // 2GB limit
        maxNumberOfFiles: 1,
        allowedFileTypes: ['video/*'],
      },
      autoProceed: true,
      doneButtonHandler: null,
    })

    uppy.use(Tus, {
      endpoint: `/api/get-upload-url`,
      resume: true,
      removeFingerprintOnSuccess: true,
    })

    uppy.on('complete', (result) => {
      const videoId = result.successful[0].uploadURL
        .split('/')
        .pop()
        .split('?')[0]
      setVideoId(videoId)
    })

    setVideoUppy(uppy)
    return uppy
  }

  useEffect(() => {
    if (!allowVideoUpload) {
      return
    }

    const uppy = configureUppy()

    return () => uppy.close()
  }, [])

  return (
    <Button
      noPadding
      primary={getValues('isVideo')}
      className="px-4 py-3 font-semibold rounded-xl hover:bg-gray-200"
      onClick={() => {
        if (!getValues('isVideo')) {
          handleSetResourceOption('isVideo')
          configureUppy()
        } else {
          handleSetResourceOption()
          setVideoId(null)
        }
      }}
      disabled={!allowVideoUpload}
    >
      <div className="flex-row flex items-center">
        <Icon
          name="play"
          size={24}
          color={getValues('isVideo') ? 'white' : 'black'}
        />
        {t('forms.common.video')}
      </div>
    </Button>
  )
}

export const UppyDashboard = ({ videoUppy, videoId }) => {
  const { t } = useTranslation()
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 768
  )

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleResize = () => setWindowWidth(window.innerWidth)
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }
  }, [])

  const dashboardHeight = windowWidth < 768 ? 300 : 200

  return (
    <div className="flex flex-col mt-3 w-full max-w-144">
      <p className="mb-2 font-bold text-black">
        {t('forms.common.uploadVideo')}
      </p>
      <Dashboard
        uppy={videoUppy}
        proudlyDisplayPoweredByUppy={false}
        width="100%"
        height={dashboardHeight}
        note={t('forms.common.videoMustBeLessThan2GB')}
        doneButtonHandler={() => null}
        theme="light"
      />
      {links?.videoCompressionGuide ? (
        <p className="text-gray-400 text-sm mt-2">
          {t('forms.common.keepWindowOpenWithCompression', {
            link: (
              <a href={links.videoCompressionGuide} className="text-topic-blue">
                {t('forms.common.tryCompressingIt')}
              </a>
            ),
          })}
        </p>
      ) : (
        <p className="text-gray-400 text-sm mt-2">
          {t('forms.common.keepWindowOpen')}
        </p>
      )}
      {videoId !== null && (
        <div className="text-gray-400 mt-1 text-sm">
          {t('forms.common.videoSuccessfullyUploaded')}
        </div>
      )}
    </div>
  )
}

export const UppyEnableMessage = ({ allowVideoUpload, videoId }) => {
  const { t } = useTranslation()

  if (allowVideoUpload || videoId || !links.supportEmail) {
    return null
  }

  return (
    <p className="text-gray-400 mt-1">
      {t('forms.common.videoPremiumFeature')}{' '}
      <a className="text-topic-blue" href={links.supportEmail}>
        {t('forms.common.enquireAboutEnabling')}
      </a>
    </p>
  )
}

export default UppyUploadButton
